import {spro_cls_boutonParam} from "../../interfaces/spro-boutonParam";
import {spro_cli_gridDetail} from "../../interfaces/spro-gridDetail";
import {ViewEncapsulation, Component, Input, Output, EventEmitter, OnInit} from '@angular/core';
import {FormGroup, FormControl, Validators} from '@angular/forms';
import {State} from '@progress/kendo-data-query';
import {GridDataResult, SelectableSettings, RowArgs} from '@progress/kendo-angular-grid';
import {Observable} from 'rxjs/Observable';
import {spro_cli_columnGrid} from '../../interfaces/spro-columnGrid';
import {spro_enum_columnType} from "../../enums/spro-columnType.enum";
import {spro_cli_detailGridSel} from "../../interfaces/spro-detailGridSel";
import {spro_cls_varGlobal} from "../../spro-varGlobal";
import {DomSanitizer, SafeStyle} from "@angular/platform-browser";
import {IntlService} from "@progress/kendo-angular-intl";

/*##################################################################################*
 * COMPOSANT SPRO : spro-grid
 *
 * DESCRIPTION : Composant permettant l'affichage d'une grid Kendo UI
 *
 * Le composant peut être utilisé seulement pour de l'affichage mais également pour
 * de la saisie au sein de la ligne de donnée
 * ou via de la saisie au sein d'une fenêtre externe si les param. sont renseignés
 ##################################################################################*/

@Component({
  selector: 'spro-grid',
  styleUrls: ['./spro-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './spro-grid.component.html'
})
export class spro_cls_grid implements OnInit{

  /** Nom de la page afin de gérer la traduction */
  @Input() w_titre_trad: string;

  /** Colonnes que contient la grid*/
  @Input() h_columns: spro_cli_columnGrid[];
  /** Paramètres de la grille */
  @Input() h_gridState: State;
  /** Données visualisées dans la grid */
  @Input() h_gridView: Observable<GridDataResult>;
  /** Hauteur du tableau */
  @Input() i_taille_tableau: number = 500;

  /** Affichage du bouton d'ajout */
  @Input() f_btAjout: boolean = false;
  /** Affichage du bouton de saisie */
  @Input() f_btModif: boolean = false;
  /** Affichage du bouton de suppression */
  @Input() f_btSuppr: boolean = false;
  /** Affichage du bouton d'export PDF */
  @Input() f_btPDF: boolean = false;
  /** Affichage du bouton d'export Excel */
  @Input() f_btExcel: boolean = false;
  /** Affichage du bouton pour masquer/afficher des champs */
  @Input() f_btMasque: boolean = true;

  /** Mode de chargement de la grid en pas à pas */
  @Input() f_frChargementPasAPas: boolean = false;

  /** Réinitialise l'enum pour qu'il soit accessible côté html */
  public spro_enum_columnType = spro_enum_columnType;

  /**
   * Constructeur
   */
  constructor(public s_intl: IntlService,
    public s_proGlobal: spro_cls_varGlobal,
    public sanitizer: DomSanitizer) {}

  /**
   * ngOnInit
   */
  ngOnInit() {
    this.h_paramSelect = {
      checkboxOnly: false,
      mode: "single"
    }; //multiple

    if (this.f_frChargementPasAPas) {
      this.f_sortable = false;
      this.w_filterable = "";
    }

    let f_trouve = false;
    this.s_proGlobal.h_lst_colummns.every((element) => {
      if (element.w_id === this.w_titre_trad) {
        f_trouve = true;
        this.h_columns = element.h_cols;
        return false;
      }
      return true;
    });
    if (!f_trouve)
      this.s_proGlobal.h_lst_colummns.push({w_id: this.w_titre_trad, h_cols: this.h_columns});
  }

  /*##################################################################################*
   * Sélection
   *##################################################################################*/

  /** Evénements sur sélection de ligne */
  @Output() e_selectRow: EventEmitter<any> = new EventEmitter();

  /** Setter pour la sélection de la ligne */
  @Input()
  set set_select_line(h_lig: any) {
    this.proc_select_line(h_lig);
  }

  /** Paramètre de sélection */
  public h_paramSelect: SelectableSettings;
  /** Stockage de la ligne sélectionnée */
  public h_selectedLine: any[] = [];

  /**
   * Procédure de sélection d'une ligne,
   * la ligne sélectionné est réinitialisé sur un stateChange
   */
  public proc_select_line(h_lig: any) {
    if (h_lig == null)
      this.h_selectedLine = [];
    else
      this.h_selectedLine = [h_lig];

    this.e_selectRow.emit(h_lig);
  }

  /**
   *  Clé de valeur d'une Sélection
   */
  public proc_selectionKeys(context: RowArgs): number {
    return context.dataItem;
  }

  /**
   * Sur sélection de ligne
   */
  public proc_selectHandler({selectedRows}) {
    this.e_selectRow.emit(selectedRows[0].dataItem);
  }

  /*##################################################################################*
   * Détails par lignes
   *##################################################################################*/

  /** Détail des lignes de la grid*/
  @Input() h_detail: spro_cli_gridDetail[];
  /** Titre des colonnes du détail de la grid*/
  @Input() h_detailTitreCol: string[];

  /**
   * Format les valeurs en fonction du type pour les zones de détail
   */
  public proc_formatDetail(w_typed: string, val: any): string {
    if (w_typed === "date") {
      return this.proc_formatDate(val);
    }
    else if (w_typed === "numeric" || w_typed === "monetaire" || w_typed === "pourcentage")
      return this.proc_formatNumber(w_typed, val);

    return val;
  }

  /*##################################################################################*
   * Détails par lignes via une grid
   *##################################################################################*/

  /** Champs du Json contenant les lignes de données du détail (Lignes enfants) */
  @Input() h_detailGrid: string = null;
  /** Colonnes de la grid dans le détail via la spro-grid-detail*/
  @Input() h_detailGrid_columns: spro_cli_columnGrid[] = null;
  /** Largeur de la colonne contenant la case à cocher de sélection */
  @Input() i_detailGridSelLargCol: number = 10;

  /** Evénements sur sélection du détail par grid */
  @Output() e_detailGridSelectRow: EventEmitter<any> = new EventEmitter();

  /** Liste des sélection du détail par grid */
  public h_detailGridSel: spro_cli_detailGridSel[];

  /**
   * Action sur sélection dans le détail par grid
   */
  public proc_detailGridSelectRow(ligSel: any, dataItem: any): void {
    if (this.h_detailGridSel == null) {
      this.h_detailGridSel = [
        {
          h_ligne: dataItem,
          h_sel: ligSel
        }
      ];
    }
    else {
      let f_trouve: boolean = false;
      this.h_detailGridSel.every((detail) => {
        if (detail.h_ligne === dataItem) {
          f_trouve = true;
          detail.h_sel = ligSel;
          return false;
        }
        return true;
      });

      if (!f_trouve) {
        this.h_detailGridSel.push({
          h_ligne: dataItem,
          h_sel: ligSel
        })
      }
    }

    this.e_detailGridSelectRow.emit(this.h_detailGridSel);
  }

  /*##################################################################################*
   * Boutons paramétrables
   *##################################################################################*/

  /** Liste des boutons paramétrables */
  @Input() h_btParam: spro_cls_boutonParam[];
  /** Largeur de la colonne contenant les boutons paramétrables */
  @Input() i_btLargCol: number = 10;
  /** Label contenant la classe utilisé pour le style du bouton */
  @Input() w_btClass: string = "k-button";
  /** Déclenchement de l'événement paramétrables */
  @Output() e_btParam: EventEmitter<any> = new EventEmitter();

  /**
   * Déclencher l'événement lié au bouton paramétrable
   */
  public proc_action_btParam(h_bt: spro_cls_boutonParam, h_data: any) {
    h_bt.h_dataLigne = h_data;
    this.e_btParam.emit(h_bt);
  }

  /**
   * Affiche/Masque le bouton en fonction de la condition
   */
  public proc_afficher_btParam(h_bt: spro_cls_boutonParam, h_data: any): boolean {
    return h_bt.a_affiche(h_data);
  }

  /*##################################################################################*
   * Formatage des données
   *##################################################################################*/

  /** Liste des formats de date sélectionnables */
  public h_formatDate: Array<any> = [{
    text: "dd-MM-yyyy", click: () => {this.proc_changeFormatDate('dd-MM-yyyy');}
  }, {
    text: "dd-MM-yyyy hh:mm", click: () => {this.proc_changeFormatDate('dd-MM-yyyy HH:mm');}
  }, {
    text: "MM/dd/yyyy", click: () => {this.proc_changeFormatDate('MM/dd/yyyy');}
  }, {
    text: "MM/dd/yyyy hh:mm", click: () => {this.proc_changeFormatDate('MM/dd/yyyy HH:mm');}
  }, {
    text: "yyyy-MM-dd", click: () => {this.proc_changeFormatDate('yyyy-MM-dd');}
  }, {
    text: "yyyy-MM-dd hh:mm", click: () => {this.proc_changeFormatDate('yyyy-MM-dd HH:mm');}
  }, {
    text: "EEEE dd MMMM yyyy", click: () => {this.proc_changeFormatDate('EEEE dd MMMM yyyy');}
  }];

  /**
   * Changement du format date
   */
  public proc_changeFormatDate(val: string): void {
    this.s_proGlobal.w_formatDate = val;
    this.proc_onStateChange(this.h_gridState);
  }

  /**
   * Affichage des décimales
   */
  public proc_BtChangeFormatNum(): void {
    // TODO : Gérer pour afficher en fonction du format les décimales
  }

  /**
   * Formate une date pour l'afficher
   */
  public proc_formatDate(val: Date): string {
    if (val == null)
      return "";

    return this.s_intl.formatDate(val, this.s_proGlobal.w_formatDate);
  }

  /**
   * Formate un nombre pour l'afficher
   */
  public proc_formatNumber(w_typed: string, val: any): string {
    if (val == null)
      return "";

    let w_retour: string = val;
    if (w_typed === "monetaire")
      w_retour = w_retour + " €";
    else if (w_typed === "pourcentage")
      w_retour = w_retour + " %";

    return w_retour;
  }

  /**
   * Retourne le filtre a utilisé en fonction du type de données
   */
  public proc_getFiltre(w_type: string): string {
    if (w_type === "monetaire" || w_type === "pourcentage")
      return "numeric";
    if (w_type === "info" || w_type === "trad")
      return "text";
    return w_type;
  }

  /**
   * Formate une liste (séparé par ;) pour l'afficher en popup d'info
   */
  public proc_formatInfo(val: string): string {
    if (val == null)
      return "";

    return val.replace(/;/g, '<br>');
  }

  /**
   * La liste contient t'elle des ;
   */
  public proc_isInfo(val: string): boolean {
    if (val == null)
      return false;

    return val.includes(";");
  }

  /**
   * Recherche la couleur de la cellule
   */
  public proc_colorCode(w_couleur: string, dataItem: any): SafeStyle {
    let result;
    if (dataItem == null
      || w_couleur == null
      || dataItem[w_couleur] == null)
      result = 'transparent';
    else
      result = dataItem[w_couleur];

    return this.sanitizer.bypassSecurityTrustStyle(result);
  }

  /*##################################################################################*
   * Action sur les colonnes
   *##################################################################################*/

  /** Affichage de l'écran d'affichage d'autres colonnes */
  public f_showEcranColumn: boolean = false;

  /**
   * Afficher / Masquer les colonnes
   */
  public proc_setVisibleCols(column: spro_cli_columnGrid): void {
    if (column.f_visible == null)
      column.f_visible = false;
    else
      column.f_visible = !column.f_visible;

    //Sauvegarde le colonnage dans les varGlobal
    this.s_proGlobal.h_lst_colummns.every(element => {
      if (element.w_id === this.w_titre_trad) {
        element.h_cols = this.h_columns;
        return false;
      }
      return true;
    });
  }

  /**
   * Afficher / Masquer la popup d'affichage de colonne
   */
  public proc_setVisibleEcranColumn(): void {
    this.f_showEcranColumn = !this.f_showEcranColumn;
  }

  /**
   * Remplace les espaces dans le texte des libellés pour la popup d'affichage de colonne
   */
  public proc_replaceSpace(field: string): string {
    return field.replace(/ /g, "_");
  }

  /*##################################################################################*
   * Changement de l'état
   *##################################################################################*/

  /** Activé le tri des colonnes */
  public f_sortable: boolean = true;
  /** Activé le filtre des colonnes, si != '', lequel */
  public w_filterable: string = "menu";

  /**
   * Sur changement du state (tri, filtre, page)
   */
  public proc_onStateChange(state: State) {
    this.proc_select_line(null); // Réinit de la sélection
    this.h_gridState = state;
    this.e_state_change.emit(state);

    if (this.h_detailGrid) {
      this.h_detailGridSel = null;
      this.e_detailGridSelectRow.emit(this.h_detailGridSel);
    }
  }

  /*##################################################################################*
   * Saisie
   *##################################################################################*/

  /** Largeur de la colonne contenant les boutons actions */
  @Input() i_largCol: number = 5;
  /** Données d'initialisation dans le cas d'un ajout */
  @Input() h_new: any = null;
  /** Sagit t'il d'une saisie ligne (false) ou d'une saisie via une frame externe (true) */
  @Input() f_frExterne: boolean = false;

  /** Evénements sur Lecture */
  @Output() e_state_change: EventEmitter<any> = new EventEmitter();
  /** Evénements sur Modification */
  @Output() e_data_save: EventEmitter<any> = new EventEmitter();
  /** Evénements sur Suppression */
  @Output() e_data_delete: EventEmitter<any> = new EventEmitter();
  /** Evénements sur ouverture d'une fenêtre externe pour la saisie */
  @Output() e_open_externe: EventEmitter<any> = new EventEmitter();

  /** Formulaire de saisie */
  public formGroup: FormGroup;
  /** Stockage de la ligne a modifier */
  public editDataItem: any;
  /** N° de la ligne a modifier */
  public editedRowIndex: number;
  /** Stockage de la ligne a supprimer, sur remplissage ouvre la fenêtre dialogue de confirmation */
  public itemToRemove = null;

  /**
   * Ajout d'une ligne
   */
  public proc_addHandler({sender}) {
    if (this.h_new == null)
      this.editDataItem = {};
    else
      this.editDataItem = this.h_new;

    if (this.f_frExterne) {
      const h_tab: {h_data: any, f_new: boolean} = {h_data: this.editDataItem, f_new: true};
      this.e_open_externe.emit(h_tab);
    }
    else {
      this.proc_closeEditor(sender);

      const h_group: any = {};
      this.h_columns.forEach(a_col => {
        if (a_col.f_saisie)
          h_group[a_col.w_champs] = a_col.f_obligatoire ? new FormControl('', Validators.required)
            : new FormControl('');
      });
      this.formGroup = new FormGroup(h_group);

      sender.addRow(this.formGroup);
    }
  }

  /**
   * Modification
   */
  public proc_editHandler({sender, rowIndex, dataItem}) {
    if (this.f_frExterne) {
      this.editDataItem = dataItem;
      const h_tab: {h_data: any, f_new: boolean} = {h_data: this.editDataItem, f_new: false};
      this.e_open_externe.emit(h_tab);
    }
    else {
      this.proc_closeEditor(sender);

      const h_group: any = {};
      this.h_columns.forEach(a_col => {
        if (a_col.f_saisie && (a_col.f_clef == null || !a_col.f_clef))
          h_group[a_col.w_champs] = a_col.f_obligatoire ? new FormControl(dataItem[a_col.w_champs], Validators.required)
            : new FormControl(dataItem[a_col.w_champs]);
      });
      this.formGroup = new FormGroup(h_group);

      this.editDataItem = dataItem;
      this.editedRowIndex = rowIndex;

      sender.editRow(rowIndex, this.formGroup);
    }
  }

  /**
   * Annulation
   */
  public proc_cancelHandler({sender, rowIndex}) {
    this.editDataItem = null;
    this.proc_closeEditor(sender, rowIndex);
  }

  /**
   * Sauvegarde
   */
  public proc_saveHandler({sender, rowIndex, formGroup, isNew}) {
    if (!this.f_frExterne) {
      for (let i = 0; i < this.h_columns.length; i++) {
        if (this.h_columns[i].f_saisie) {
          if (!this.h_columns[i].f_clef || isNew) {
            this.editDataItem[this.h_columns[i].w_champs] = formGroup.value[this.h_columns[i].w_champs];
          }
        }
      }
    }

    const h_tab: {h_data: any, f_new: boolean} = {h_data: this.editDataItem, f_new: isNew};

    this.e_data_save.emit(h_tab);
    this.editDataItem = null;

    if (!this.f_frExterne)
      sender.closeRow(rowIndex);
  }

  /**
   * Suppression
   */
  public proc_removeHandler({dataItem}) {
    this.itemToRemove = dataItem;
  }

  /**
   * Fenêtre de confirmation sur suppression
   */
  public proc_confirmRemove(f_conf: boolean): void {
    if (f_conf) {
      this.e_data_delete.emit(this.itemToRemove);
    }

    this.itemToRemove = null;
  }

  /**
   * Fermeture de la saisie
   */
  public proc_closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = null;
    this.formGroup = null;
  }
}



// WEBPACK FOOTER //
// C:/spro/Workspaces/VisualCode/Ecoltex2/src/app/sproLib/spro/grids/spro-grid/spro-grid.component.ts